/**
 * kartBaskisi.js — the printed entrance card (backlog #745, built on backend #725).
 *
 * The card goes onto PRE-PRINTED red card stock (portrait, about 10 x 15 cm: red band on top, a white window, group logos and a red footer),
 * through the desk's printer, and must land where the OLD PHP card (views/modals/kisi_kart_yazdir.php) landed with the desk's printer settings
 * as they are. So this copies that page's geometry in the same CSS px and sets NO @page rule and no paper size: the print dialog's margins and
 * scale (100 %) decide, exactly as before. Measured off Deniz's photo of real cards (2026-11-05, +/- 3-5 mm: calibrate with a ruler on the
 * first real print, then adjust the constants below):
 *   - a 440 px wide box whose content starts 100 px down, inside the page's default 8 px body margin;
 *   - ONE table, four columns 80 / 150 / 150 / 40 (scaled to the 440 px box), 8 px cell padding, everything centred:
 *     (1) the fair logo turned 270 degrees, (2) the photo (min 150 px high, 2 px black frame),
 *     (3) the QR's place (100 px; no QR for now, the room stays) + the name in bold + the kimlik no + the meslek in 12 px,
 *     (4) the yellow strip (#FCDE06, 20 px bold) turned 270 degrees with the fair's start month and year, 'Eylül - 2026';
 *   - with an Özel İzin a second row: a red banner with its type under columns 2-3.
 * Cuts as PHP: the name 26 characters, the meslek 60. Upper case as on the live card.
 *
 * The card is drawn in its OWN document (a hidden iframe), so the app's CSS (Velzon / Bootstrap 5) cannot move it and the page's default
 * body margin is the same one the old page had. Nothing is stored: the card is made from GET .../kart-basimlari/{id}/kart every time.
 */
import i18n, { currentLocaleTag } from "../../i18n";
import { fetchKisiFotograf } from "../../services/personelRosterService";
import { fetchFuarLogosu, getKartBaskisi } from "../../services/cardAccessService";

// ---- the old card's geometry, in CSS px (all in one place for the desk's calibration) -------------------------------------------------
export const KART_OLCU = {
  kutuGenislik: 440,
  kutuYukseklik: 240,
  icerikUstBosluk: 100,
  sutunlar: [80, 150, 150, 40],
  hucreBoslugu: 8,
  fotoEnAz: 150,
  fotoCerceve: 2,
  qrKutusu: 100, // the QR's place; the room is kept without a QR so the name stays where the old card put it
  seritRenk: "#FCDE06",
  izinRenk: "#d9534f",
  adKesim: 26,
  meslekKesim: 60,
};
/** false = the name moves up into the QR's place (when Deniz wants no room kept). */
export const QR_YERI_AYIR = true;

const kes = (metin, n) => Array.from(String(metin ?? "")).slice(0, n).join("");
const buyuk = (metin, yerel) => String(metin ?? "").toLocaleUpperCase(yerel);
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const ingilizce = () => String(currentLocaleTag()).toLowerCase().startsWith("en");

/** "2026-10-02" or "2026-10-02T14:30:00" as a short local date (and time when the window has one). */
export function gecerlilikTarihi(deger) {
  if (!deger) return null;
  const s = String(deger);
  if (s.includes("T")) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? s : d.toLocaleString(currentLocaleTag(), { dateStyle: "short", timeStyle: "short" });
  }
  const [y, m, g] = s.split("-").map(Number);
  return y && m && g ? new Date(y, m - 1, g).toLocaleDateString(currentLocaleTag(), { dateStyle: "short" }) : s;
}

/** What the card says in each place, as plain strings (already upper-cased and cut). */
export function kartSatirlari(kart, { gecerlilikYaz = false } = {}) {
  const kisi = kart?.kisi ?? {};
  const yerel = ingilizce() ? "en-US" : "tr-TR";
  // A Turkish citizen's name is upper-cased in Turkish (i -> İ); a passport holder's is not (Mike must not become MİKE).
  const adYereli = kisi.kimlikTuru === "TC_KIMLIK" ? "tr-TR" : "en-US";
  const ad = kes(buyuk(kisi.adSoyad ?? [kisi.ad, kisi.soyad].filter(Boolean).join(" "), adYereli), KART_OLCU.adKesim);

  // Çalışan / Employee when the print has no meslek (PHP printed nothing there). Kategori big, meslek in brackets, as the live card's
  // 'ELEKTRİK SORUMLUSU (TEKNİKER)'.
  const meslek = kart?.meslek;
  let meslekYazisi;
  if (!meslek || kart?.calisan) {
    meslekYazisi = i18n.t("kart:kartBaskisi.calisan");
  } else {
    const kategori = meslek.kategoriAd && meslek.kategoriAd !== meslek.ad ? meslek.kategoriAd : null;
    meslekYazisi = kategori ? `${kategori} (${meslek.ad})` : meslek.ad;
  }
  meslekYazisi = kes(buyuk(meslekYazisi, yerel), KART_OLCU.meslekKesim);

  const g = kart?.gecerlilik;
  const gecerlilikTarih = gecerlilikYaz && g && !g.erisimBilinmiyor ? gecerlilikTarihi(g.accessValidUntil || g.fuarBitis || kart?.fuar?.bitis) : null;

  return {
    ad,
    kimlikNo: kisi.kimlikNo ? String(kisi.kimlikNo) : "",
    meslek: meslekYazisi,
    serit: kart?.fuar?.serit?.tr ?? "", // the month in Turkish as PHP did (the English one is in the answer, unused)
    ozelIzin: kart?.kartOzelIzinle && kart?.ozelIzin ? (kart.ozelIzin.ad ?? kart.ozelIzin.adTr ?? "") : "",
    gecerlilik: gecerlilikTarih ? i18n.t("kart:kartBaskisi.gecerlilik", { tarih: gecerlilikTarih }) : "",
  };
}

function css() {
  const o = KART_OLCU;
  const toplam = o.sutunlar.reduce((a, b) => a + b, 0);
  return `
*{box-sizing:border-box}
html{-webkit-print-color-adjust:exact;print-color-adjust:exact}
body{margin:8px;font-family:"Open Sans","Helvetica Neue",Helvetica,Arial,sans-serif;font-size:14px;line-height:1.42857143;color:#333;text-align:center;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.cikti{width:${o.kutuGenislik}px;height:${o.kutuYukseklik}px;padding-top:${o.icerikUstBosluk}px}
table{width:${o.kutuGenislik}px;table-layout:fixed;border-collapse:collapse}
td{padding:${o.hucreBoslugu}px;vertical-align:middle;text-align:center}
.logo{position:relative;width:100%;height:${o.fotoEnAz}px}
.logo img{position:absolute;left:50%;top:50%;width:${o.sutunlar[0]}px;height:auto;transform:translate(-50%,-50%) rotate(270deg)}
.foto{border:${o.fotoCerceve}px solid #000;min-height:${o.fotoEnAz}px}
.foto img{display:block;width:100%;height:auto}
.qr{height:${o.qrKutusu}px}
.ad{font-weight:700;margin-top:4px}
.kimlik{font-size:14px}
.meslek{font-size:12px}
.gecerlilik{font-size:11px;margin-top:2px}
.serit-kap{position:relative;width:100%;height:${o.fotoEnAz}px}
.serit{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%) rotate(270deg);white-space:nowrap;background:${o.seritRenk};font-size:20px;font-weight:700;line-height:1.42857143}
.izin{background:${o.izinRenk};color:#fff;font-size:12px;padding:2px 6px}
col.c0{width:${(o.kutuGenislik * o.sutunlar[0] / toplam).toFixed(2)}px}
col.c1{width:${(o.kutuGenislik * o.sutunlar[1] / toplam).toFixed(2)}px}
col.c2{width:${(o.kutuGenislik * o.sutunlar[2] / toplam).toFixed(2)}px}
col.c3{width:${(o.kutuGenislik * o.sutunlar[3] / toplam).toFixed(2)}px}
`;
}

/**
 * The card's document. `logoUrl` / `fotoUrl` are object URLs (null = an empty frame, never a broken icon).
 * Pure: the same input gives the same markup (tested).
 */
export function kartBaskisiHtml(kart, { fotoUrl = null, logoUrl = null, gecerlilikYaz = false } = {}) {
  const s = kartSatirlari(kart, { gecerlilikYaz });
  const baslik = esc(i18n.t("kart:kartBaskisi.kartBasligi"));
  return `<!doctype html><html lang="${ingilizce() ? "en" : "tr"}"><head><meta charset="utf-8"><title>${baslik}</title><style>${css()}</style></head><body>
<div class="cikti"><table>
<colgroup><col class="c0"><col class="c1"><col class="c2"><col class="c3"></colgroup>
<tr>
<td><div class="logo">${logoUrl ? `<img src="${esc(logoUrl)}" alt="">` : ""}</div></td>
<td><div class="foto">${fotoUrl ? `<img src="${esc(fotoUrl)}" alt="">` : ""}</div></td>
<td>${QR_YERI_AYIR ? '<div class="qr"></div>' : ""}<div class="ad">${esc(s.ad)}</div>${s.kimlikNo ? `<div class="kimlik">${esc(s.kimlikNo)}</div>` : ""}<div class="meslek">${esc(s.meslek)}</div>${s.gecerlilik ? `<div class="gecerlilik">${esc(s.gecerlilik)}</div>` : ""}</td>
<td><div class="serit-kap">${s.serit ? `<div class="serit">${esc(s.serit)}</div>` : ""}</div></td>
</tr>${s.ozelIzin ? `
<tr><td></td><td colspan="2"><div class="izin">${esc(s.ozelIzin)}</div></td><td></td></tr>` : ""}
</table></div>
</body></html>`;
}

/**
 * Everything the card needs from the network: the print's card data, then the photo and the fair logo as object URLs (each only when the card
 * says there is one; a failure of either leaves its frame empty). The caller revokes the URLs (`kartGorselleriniBirak`).
 */
export async function kartBaskisiHazirla(personId, kartBasimId) {
  const kart = await getKartBaskisi(personId, kartBasimId);
  const [fotoUrl, logoUrl] = await Promise.all([
    kart?.kisi?.fotografVar ? fetchKisiFotograf(personId).catch(() => null) : null,
    kart?.fuar?.logoYolu ? fetchFuarLogosu(kart.fuar.fuarId ?? kart.fuarId) : null,
  ]);
  return { kart, fotoUrl, logoUrl };
}

export function kartGorselleriniBirak({ fotoUrl, logoUrl } = {}) {
  [fotoUrl, logoUrl].forEach((u) => { if (u) URL.revokeObjectURL(u); });
}

/** Waits until every image of the card document has loaded; an image that fails is hidden (an empty frame). */
function resimleriBekle(doc) {
  return Promise.all(Array.from(doc.images).map((img) => (img.complete
    ? Promise.resolve()
    : new Promise((tamam) => {
      img.addEventListener("load", tamam, { once: true });
      img.addEventListener("error", () => { img.style.visibility = "hidden"; tamam(); }, { once: true });
    }))));
}

/**
 * Opens the browser's print dialog for the card ALONE: a hidden iframe holds the card's document. Resolves once the dialog was opened (Chrome
 * returns when it is closed). The iframe is removed after printing.
 */
export async function kartiYazdir(html) {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.tabIndex = -1;
  Object.assign(iframe.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
  document.body.appendChild(iframe);
  let bitti = false;
  const temizle = () => { if (!bitti) { bitti = true; iframe.remove(); } };
  try {
    const win = iframe.contentWindow;
    const doc = win.document;
    doc.open();
    doc.write(html);
    doc.close();
    await resimleriBekle(doc);
    win.addEventListener("afterprint", temizle, { once: true });
    win.focus();
    win.print();
    setTimeout(temizle, 120000); // a browser that never fires afterprint
  } catch (e) {
    temizle();
    throw e;
  }
}
